import React, { useEffect, useState } from "react";
import { Smartphone, Download, X } from "lucide-react";

/**
 * PWA install prompt – shown only inside Admin area, only if installable & not yet installed.
 */
export default function InstallPrompt() {
  const [deferred, setDeferred] = useState(null);
  const [visible, setVisible] = useState(false);
  const [iosHint, setIosHint] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") return;
    // Hide if already running as installed PWA
    const isStandalone =
      window.matchMedia && window.matchMedia("(display-mode: standalone)").matches;
    if (isStandalone || window.navigator.standalone) return;

    // Hide if dismissed in last 7 days
    try {
      const dismissed = localStorage.getItem("aa_install_dismissed");
      if (dismissed && Date.now() - parseInt(dismissed, 10) < 7 * 24 * 3600 * 1000) return;
    } catch (e) {}

    // iOS Safari: no beforeinstallprompt; show hint instead
    const ua = window.navigator.userAgent;
    const isIos = /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
    if (isIos) {
      setIosHint(true);
      setVisible(true);
      return;
    }

    const handler = (e) => {
      e.preventDefault();
      setDeferred(e);
      setVisible(true);
    };
    window.addEventListener("beforeinstallprompt", handler);
    return () => window.removeEventListener("beforeinstallprompt", handler);
  }, []);

  const install = async () => {
    if (!deferred) return;
    deferred.prompt();
    await deferred.userChoice;
    setDeferred(null);
    setVisible(false);
  };

  const dismiss = () => {
    try { localStorage.setItem("aa_install_dismissed", Date.now().toString()); } catch (e) {}
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div
      role="dialog"
      data-testid="install-prompt"
      className="fixed inset-x-2 sm:inset-x-auto sm:right-4 bottom-4 z-50 max-w-sm bg-ink text-sand rounded-lg shadow-xl border border-white/10 p-4 animate-fade-up"
    >
      <button onClick={dismiss} className="absolute top-2 right-2 p-1 text-sand/60 hover:text-sand" aria-label="Schliessen">
        <X className="w-4 h-4" strokeWidth={1.5} />
      </button>
      <div className="flex items-start gap-3 pr-6">
        <div className="shrink-0 w-10 h-10 rounded-full bg-terracotta/20 flex items-center justify-center">
          <Smartphone className="w-5 h-5 text-terracotta-soft" strokeWidth={1.5} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-display text-base">Als App installieren</div>
          {iosHint ? (
            <p className="text-xs text-sand/75 mt-1 leading-relaxed">
              Tippe in Safari auf <strong>Teilen</strong> → <strong>"Zum Home-Bildschirm"</strong>.
            </p>
          ) : (
            <p className="text-xs text-sand/75 mt-1 leading-relaxed">
              Schneller Zugriff aufs Admin-Dashboard direkt vom Homescreen.
            </p>
          )}
          {!iosHint && (
            <button
              onClick={install}
              data-testid="install-prompt-btn"
              className="mt-3 inline-flex items-center gap-2 px-4 py-2 bg-terracotta text-white rounded-full text-sm hover:bg-terracotta-hover"
            >
              <Download className="w-3.5 h-3.5" strokeWidth={1.5} />
              Installieren
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
